import { NgModule, inject, provideAppInitializer } from '@angular/core';
import { MatSelectModule } from '@angular/material/select';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipDefaultOptions } from '@angular/material/tooltip';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { ReactiveFormsModule } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { AppComponent } from './app.component';
import { ModulesService } from './modules.service';
import { provideHttpClient, withInterceptorsFromDi, HTTP_INTERCEPTORS } from '@angular/common/http';
import { CredentialsInterceptor } from './base/networking/credentialsInterceptor';
import { ConfigApiInterceptor } from './base/networking/configApiInterceptor';
import { LoginGuard } from './login.guard';
import { AuthGuard } from './auth.guard';
import { LayoutModule } from '@angular/cdk/layout';
import { MaterialModule } from './material/material.module';
import { FlexLayoutModule } from '@angular/flex-layout';
import { ModulesComponent } from './modules/modules.component';
import { ClientsComponent } from './clients/clients.component';
import { ClientsService } from './clients.service';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { SchedulerComponent } from './scheduler/scheduler.component';
import { ImportDataComponent } from './views/import-data/import-data.component';
import { FileBrowserDialogComponent } from './views/file-browser-dialog/file-browser-dialog.component';
import {ScrollingModule} from '@angular/cdk/scrolling';
import { ConfigService } from './services/config/config.service';
import { SaveMappingConfigDialogComponent } from './views/save-mapping-config-dialog/save-mapping-config-dialog.component';
import { PreviewTableComponent } from './views/preview-table/preview-table.component';
import { ErrorReportingService } from './services/errorReporting/error-reporting.service';
import { ErrorDialogComponent } from './views/error-dialog/error-dialog.component';
import { BaseComponent } from './base/base.component';
import { NewScheduleDialogComponent } from './scheduler/new-schedule-dialog/new-schedule-dialog.component';
import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';
import { DatePipe } from '@angular/common';
import { ConfirmDialogComponent } from './framework/confirm-dialog/confirm-dialog.component';
import { RestApiDataConnectorComponent } from './views/connectors/restApi/rest-api-data-connector/rest-api-data-connector.component';
import { FileDataConnectorComponent } from './views/connectors/file/file-data-connector/file-data-connector.component';
import { ConfigListViewComponent } from './views/config-list-view/config-list-view.component';
import { TransformerListComponent } from './views/transformer-list/transformer-list.component';
import { WriteDatabaseTransformerDetailComponent } from './views/transform/transformerDetail/write-database-transformer-detail/write-database-transformer-detail.component';
import { DataTransformConfigLayoutComponent } from './views/dataTransformConfig/data-transform-config-layout/data-transform-config-layout.component';
import { IngestWorkFlowLayoutComponent } from './views/dataTransformConfig/ingest/ingest-work-flow-layout/ingest-work-flow-layout.component';
import { MapperFlowLayoutComponent } from './views/dataTransformConfig/mapper/mapper-flow-layout/mapper-flow-layout.component';
import { TransformerWorkFlowLayoutComponent } from './views/dataTransformConfig/transformers/transformer-work-flow-layout/transformer-work-flow-layout.component';
import { ConfigReviewWorkFlowLayoutComponent } from './views/dataTransformConfig/review/config-review-work-flow-layout/config-review-work-flow-layout.component';
import { NewSingleStringValueDialogComponent } from './views/shared/new-single-string-value-dialog/new-single-string-value-dialog.component';
import { ExtractorTransformDetailComponent } from './views/transform/transformerDetail/extractor-transform-detail/extractor-transform-detail.component';
import { NavigationService } from './services/navigation/navigation.service';
import { ColumnResizeDirective } from './column-resize.directive';
import { DndDirective } from './base/directives/dragAndDrop.directive';
import { EnvironmentService } from './services/environment/environment.service';
import { SettingsComponent } from './views/settings/settings.component';
import { DeveloperSettingsComponent } from './views/settings/developer-settings/developer-settings.component';
import { EnvironmentSettingsComponent } from './views/settings/environment-settings/environment-settings.component';
import { CopilotSettingsComponent } from './views/settings/copilot-settings/copilot-settings.component';
import { ConfigDocumentTreeComponent } from './views/config/config-document-tree/config-document-tree.component';
import { ConfigDetailComponent } from './views/config/config-detail/config-detail.component';
import { BusyIndicatorComponent } from './views/shared/busy-indicator/busy-indicator.component';
import { ActivityNamingTransformDetailComponent } from './views/transform/transformerDetail/activity-naming-transform-detail/activity-naming-transform-detail.component';
import { ExcelDataConnectorComponent } from './views/connectors/excel/excel-data-connector/excel-data-connector.component';
import { DevelopmentDataComponent } from './views/development-data/development-data.component';
import { IngestFileUploadComponent } from './views/development-data/ingest-file-upload/ingest-file-upload.component';
import { IngestFileTreeComponent } from './views/development-data/ingest-file-tree/ingest-file-tree.component';
import { EventGroupNamerTransformDetailComponent } from './views/transform/transformerDetail/event-group-namer-transform-detail/event-group-namer-transform-detail.component';
import { EventNamerTransformDetailComponent } from './views/transform/transformerDetail/event-namer-transform-detail/event-namer-transform-detail.component';
import { DataDictionaryService } from './services/dataDictionary/data-dictionary.service';
import { ColumnFilterComponent } from './views/previewTable/filterUI/column-filter/column-filter.component';
import { GroupAttributorTransformDetailComponent } from './views/transform/transformerDetail/group-attributor-transform-detail/group-attributor-transform-detail.component';
import { ColumnCalcTransformDetailComponent } from './views/transform/transformerDetail/column-calc-transform-detail/column-calc-transform-detail.component';
import { TransformPipelineService } from './services/transformPipeline/transform-pipeline.service';
import { TransformPipelineDialogComponent } from './views/transform/transform-pipeline-dialog/transform-pipeline-dialog.component';
import { BaseTransformerUIComponent } from './views/dataTransformConfig/transformers/base/base-transformer-ui/base-transformer-ui.component';
import { SortBuilderComponent } from './views/transform/sort-builder/sort-builder.component';
import { ConfigViewModelService } from './services/configViewModel/config-view-model.service';
import { TransformDetailJsonViewerComponent } from './views/transform-detail-json-viewer/transform-detail-json-viewer.component';
import { EventBrokerService } from './services/eventBroker/event-broker.service';
import { DataTransformVisualizerComponent } from './views/data-transform-visualizer/data-transform-visualizer.component';
import { FieldValidationErrorViewComponent } from './views/shared/fieldValidation/field-validation-error-view/field-validation-error-view.component';
import { ErrorLogComponent } from './views/errors/error-log/error-log.component';
import { ErrorLogDetailViewComponent } from './views/errors/error-log-detail-view/error-log-detail-view.component';
import { NodedefinitionComponent } from './nodedefinition/nodedefinition.component';
import { NgxGraphModule } from '@swimlane/ngx-graph';
import { DialogBlankCheckComponent } from './nodedefinition/dialog-blank-check/dialog-blank-check.component'
import { MatExpansionModule } from '@angular/material/expansion';
import { SaveRefineryConfigDialogComponent } from './nodedefinition/save-refinery-config-dialog/save-refinery-config-dialog.component';
import { ColumnLookupTransformDetailComponent } from './views/transform/transformerDetail/column-lookup-transform-detail/column-lookup-transform-detail.component';
import { AppEnvService } from './services/app-env.service';
import { IStorageService, StorageService } from './services/storage.service';
import { IncludeBuilderComponent } from './views/transform/include-builder/include-builder.component';
import { AboutThisNodeComponent } from './nodedefinition/about-this-node/about-this-node.component';
import { FarmConverterTransformDetailComponent } from './views/transform/transformerDetail/farm-converter-transform-detail/farm-converter-transform-detail.component';
import { AutowidthDirective } from './views/directive/autowidth.directive';
import { MatTableAdjustRowHeightDirective } from './views/directive/mat-table-adjust-row-height.directive';
import { MatTableColumnResizeDirective } from './views/directive/mat-table-column-resize.directive';
import { TwoRowHeaderDirective } from './views/directive/two-row-header.directive';
import { PendingChangesGuard } from './guards/pending-changes.guard';
import { LeaveConfirmDialogComponent } from './views/shared/leave-confirm-dialog/leave-confirm-dialog.component';
import { DevsettingsComponent } from './devsettings/devsettings.component';
import { ExportOutputDataComponent } from './views/transform/export-output-data/export-output-data.component';
import { ExportOutputDataDynamicComponent } from './views/transform/export-output-data-dynamic/export-output-data-dynamic.component';
import { FolderBrowserDialogComponent } from './views/folder-browser-dialog/folder-browser-dialog.component';
import { RunNodeDialogComponent } from './nodedefinition/run-node-dialog/run-node-dialog.component';
import { RunRefineryeDialogComponent } from './nodedefinition/run-refinery-dialog/run-refinery-dialog.component';
import { RefreshComponent } from './views/refresh/refresh.component';
import { TransformerParametersComponent } from './views/transform/transformerDetail/transformer-parameters/transformer-parameters.component';
import { TransformerTopHeaderComponent } from './views/transform/transformer-top-header/transformer-top-header.component';
import { TransformerDescriptionComponent } from './views/transform/transformerDetail/transformer-description/transformer-description.component';
import { QuillModule } from 'ngx-quill';
import { AutocompleteFieldDeleteFilterComponent } from './views/shared/autocomplete-field-delete-filter/autocomplete-field-delete-filter.component';
import { AutocompleteFieldComponent } from './views/shared/autocomplete-field/autocomplete-field.component';
import { JoinerTransformDetailComponent } from './views/transform/transformerDetail/joiner-transform-detail/joiner-transform-detail.component';
import { ContainsTextDialogOptionComponent } from './views/shared/contains-text-dialog-option/contains-text-dialog-option.component';
import { LoginComponent } from './views/login/login/login.component';
import { ForgotPasswordComponent } from './views/forgot-password/forgot-password.component';
import { RowFillerTransformDetailComponent } from './views/transform/transformerDetail/row-filler-transform-detail/row-filler-transform-detail.component';
import { UserListViewComponent } from './views/user-list-view/user-list-view.component';
import { CreateUserDialogComponent } from './views/create-user-dialog/create-user-dialog.component';
import { IntegerOnlyDirective } from './views/directive/integer-only.directive';
import { RunConfirmDialogComponent } from './framework/run-confirm-dialog/run-confirm-dialog.component';
import { IntegerPositiveOnlyDirective } from './views/directive/integer-positive-only.directive';
import { OrganizationListViewComponent } from './views/organization/organization-list-view/organization-list-view.component';
import { CreateOrganizationDialogComponent } from './views/organization/create-organization-dialog/create-organization-dialog.component';
import { InfoDialogComponent } from './views/info-dialog/info-dialog.component';
import { SelectOrganizationDialogComponent } from './views/organization/select-organization-dialog/select-organization-dialog.component';
import { TypeDataConnectorComponent } from './views/connectors/data/type-data-connector/type-data-connector.component';
import { DuplicateDataConnectorComponent } from './views/connectors/duplicate/duplicate-data-connector/duplicate-data-connector.component';
import { DurationcalcsDataConnectorComponent } from './views/connectors/duration/durationcalcs-data-connector/durationcalcs-data-connector.component';
import { ParameterDataConnectorComponent } from './views/connectors/general/parameter-data-connector/parameter-data-connector.component';
import { ImportDataIngestComponent } from './views/import-data-ingest/import-data-ingest/import-data-ingest.component';
import { CreateCompanyuserDialogComponent } from './views/create-companyuser-dialog/create-companyuser-dialog/create-companyuser-dialog.component';
import { OrganizationsUsersComponent } from './views/organization/organizations-users/organizations-users.component';
import { UserProfileComponent } from './views/organization/user-profile/user-profile.component';
import { ChangePasswordComponent } from './views/change-password/change-password.component';
import { CreateAdminDialogComponent } from './views/create-admin-dialog/create-admin-dialog.component';
import { EmailSenderComponent } from './views/email-sender/email-sender.component';
import { ResetPasswordComponent } from './views/reset-password/reset-password.component';
import { FeatherComponent } from './views/connectors/feather/feather.component';
import { OrganizationWarningDialogComponent } from './views/organization/organization-warning-dialog/organization-warning-dialog.component';
import { AnonymizerTransformDetailComponent } from './views/transform/transformerDetail/anonymizer-transform-detail/anonymizer-transform-detail.component';
import { AutocompleteFieldCheckboxFilterComponent } from './views/shared/autocomplete-field-checkbox-filter/autocomplete-field-checkbox-filter.component';
import { UserChangePasswordComponent } from './views/user-list-view/user-change-password/user-change-password.component';
import { UserUsageHistoryDialogComponent } from './views/user-list-view/user-usage-history-dialog/user-usage-history-dialog.component';
import { ColumnCleanerTransformDetailComponent } from './views/transform/transformerDetail/column-cleaner-transform-detail/column-cleaner-transform-detail.component';
import { OracleDataConnectorComponent } from './views/connectors/database/oracle-data-connector/oracle-data-connector.component';
import { SqlDataConnectorComponent } from './views/connectors/database/sql-data-connector/sql-data-connector.component';
import { MysqlDataConnectorComponent } from './views/connectors/database/mysql-data-connector/mysql-data-connector.component';
import { VerticaDataConnectorComponent } from './views/connectors/database/vertica-data-connector/vertica-data-connector.component';
import { OtherDataConnectorComponent } from './views/connectors/database/other-data-connector/other-data-connector.component';
import { AutocompleteFieldSeparatorComponent } from './views/shared/autocomplete-field-separator/autocomplete-field-separator.component';
import { ValueEntryFieldComponent } from './views/shared/value-entry-field/value-entry-field.component';
import { RangeEntryFieldComponent } from './views/shared/range-entry-field/range-entry-field.component';
import { TextEntryFieldComponent } from './views/shared/text-entry-field/text-entry-field.component';
import { OrganizationsSessiontimeoutComponent } from './views/organization/organizations-sessiontimeout/organizations-sessiontimeout.component';
import { OkDialogComponent } from './views/shared/ok-dialog/ok-dialog.component';
import { TransformerErrorDialogComponent } from './views/shared/transformer-error-dialog/transformer-error-dialog.component';
import { DeactivateOrganizationDialogComponent } from './views/organization/deactivate-organization-dialog/deactivate-organization-dialog.component';
import { AutologoutConfirmDialogComponent } from './views/shared/autologout-confirm-dialog/autologout-confirm-dialog.component';
import { SelectFilterOptionComponent } from './views/shared/select-filter-option/select-filter-option.component';
import { UserListViewUnassignedComponent } from './views/user-list-view-unassigned/user-list-view-unassigned.component';
import { FindReplaceChooserMapDialogOptionComponent } from './views/shared/find-replace-chooser-map-dialog-option/find-replace-chooser-map-dialog-option.component';
import { AutocompleteFieldWithMatformComponent } from './views/shared/autocomplete-field-with-matform/autocomplete-field-with-matform.component';
import { ParquetDataConnectorComponent } from './views/connectors/parquet-data-connector/parquet-data-connector.component';
import { ProgressBusyIndicatorComponent } from './views/shared/progress-busy-indicator/progress-busy-indicator.component';
import { JsonPasteDialogComponent } from './views/shared/json-paste-dialog/json-paste-dialog.component';
import { SmartSampleDialogComponent } from './views/shared/smart-sample-dialog/smart-sample-dialog.component';
import { AuditCompilerDialogComponent, AuditRuleDetailDialogComponent } from './views/shared/audit-compiler-dialog/audit-compiler-dialog.component';
import { FileUploadDialogComponent } from './views/shared/file-upload-dialog/file-upload-dialog.component';
import { ImportRefineryDialogComponent } from './views/shared/import-refinery-dialog/import-refinery-dialog.component';
import { DelimiterDialogComponent } from './views/shared/delimiter-dialog/delimiter-dialog.component';
import { FtpServiceFormComponent } from './views/shared/ftp-service-form/ftp-service-form.component';
import { FtpServiceSelectComponent } from './views/shared/ftp-service-select/ftp-service-select.component';
import { OrganizationSettingsComponent } from './views/settings/organization-settings/organization-settings.component';
import { FtpSetupComponent } from './views/settings/organization-settings/ftp-setup/ftp-setup.component';
import { FtpServiceDialogComponent } from './views/settings/organization-settings/ftp-setup/ftp-service-dialog/ftp-service-dialog.component';
import { ExpressionEditorComponent } from './views/shared/expression-editor/expression-editor.component';
import { MaplibraryComponent } from './views/shared/maplibrary/maplibrary.component';
import { AutocompleteFieldMapperComponent } from './views/shared/autocomplete-field-mapper/autocomplete-field-mapper.component';
import { ParquetpartitionFolderBrowserDialogComponent } from './views/parquetpartition-folder-browser-dialog/parquetpartition-folder-browser-dialog.component';
import { CopilotModule } from './copilot/copilot.module';

// Global tooltip behavior: every matTooltip in the app (including hover hints
// like Min/Max Length and Consolidation) waits 2 seconds of hover before
// showing, rather than each tooltip needing its own [matTooltipShowDelay].
// hideDelay/touchendHideDelay are left at Angular Material's own defaults.
export function matTooltipDefaults(): MatTooltipDefaultOptions {
    return {
        showDelay: 2000,
        hideDelay: 0,
        touchendHideDelay: 1500,
    };
}

@NgModule({ declarations: [
        AppComponent,
        ModulesComponent,
        ClientsComponent,
        SchedulerComponent,
        ImportDataComponent,
        FileBrowserDialogComponent,
        SaveMappingConfigDialogComponent,
        PreviewTableComponent,
        ErrorDialogComponent,
        BaseComponent,
        NewScheduleDialogComponent,
        ConfirmDialogComponent,
        SqlDataConnectorComponent,
        RestApiDataConnectorComponent,
        FileDataConnectorComponent,
        ConfigListViewComponent,
        TransformerListComponent,
        WriteDatabaseTransformerDetailComponent,
        DataTransformConfigLayoutComponent,
        IngestWorkFlowLayoutComponent,
        MapperFlowLayoutComponent,
        TransformerWorkFlowLayoutComponent,
        ConfigReviewWorkFlowLayoutComponent,
        NewSingleStringValueDialogComponent,
        ExtractorTransformDetailComponent,
        ColumnResizeDirective,
        SettingsComponent,
        DeveloperSettingsComponent,
        EnvironmentSettingsComponent,
        CopilotSettingsComponent,
        ConfigDocumentTreeComponent,
        ConfigDetailComponent,
        BusyIndicatorComponent,
        ActivityNamingTransformDetailComponent,
        ExcelDataConnectorComponent,
        DevelopmentDataComponent,
        IngestFileUploadComponent,
        IngestFileTreeComponent,
        EventGroupNamerTransformDetailComponent,
        EventNamerTransformDetailComponent,
        ColumnFilterComponent,
        GroupAttributorTransformDetailComponent,
        ColumnCalcTransformDetailComponent,
        TransformPipelineDialogComponent,
        BaseTransformerUIComponent,
        SortBuilderComponent,
        TransformDetailJsonViewerComponent,
        DataTransformVisualizerComponent,
        FieldValidationErrorViewComponent,
        ErrorLogComponent,
        ErrorLogDetailViewComponent,
        NodedefinitionComponent,
        DialogBlankCheckComponent,
        SaveRefineryConfigDialogComponent,
        ColumnLookupTransformDetailComponent,
        IncludeBuilderComponent,
        AboutThisNodeComponent,
        FarmConverterTransformDetailComponent,
        AutowidthDirective,
        MatTableAdjustRowHeightDirective,
        MatTableColumnResizeDirective,
        TwoRowHeaderDirective,
        LeaveConfirmDialogComponent,
        ExportOutputDataComponent,
        ExportOutputDataDynamicComponent,
        FolderBrowserDialogComponent,
        RunNodeDialogComponent,
        RunRefineryeDialogComponent,
        RefreshComponent,
        TransformerParametersComponent,
        TransformerTopHeaderComponent,
        TransformerDescriptionComponent,
        AutocompleteFieldDeleteFilterComponent,
        AutocompleteFieldComponent,
        JoinerTransformDetailComponent,
        ContainsTextDialogOptionComponent,
        LoginComponent,
        ForgotPasswordComponent,
        RowFillerTransformDetailComponent,
        UserListViewComponent,
        CreateUserDialogComponent,
        IntegerOnlyDirective,
        RunConfirmDialogComponent,
        IntegerPositiveOnlyDirective,
        OrganizationListViewComponent,
        CreateOrganizationDialogComponent,
        InfoDialogComponent,
        SelectOrganizationDialogComponent,
        TypeDataConnectorComponent,
        DuplicateDataConnectorComponent,
        DurationcalcsDataConnectorComponent,
        ParameterDataConnectorComponent,
        ImportDataIngestComponent,
        CreateCompanyuserDialogComponent,
        OrganizationsUsersComponent,
        UserProfileComponent,
        ChangePasswordComponent,
        CreateAdminDialogComponent,
        EmailSenderComponent,
        ResetPasswordComponent,
        FeatherComponent,
        OrganizationWarningDialogComponent,
        AnonymizerTransformDetailComponent,
        AutocompleteFieldCheckboxFilterComponent,
        UserChangePasswordComponent,
        UserUsageHistoryDialogComponent,
        ColumnCleanerTransformDetailComponent,
        OracleDataConnectorComponent,
        MysqlDataConnectorComponent,
        VerticaDataConnectorComponent,
        OtherDataConnectorComponent,
        AutocompleteFieldSeparatorComponent,
        ValueEntryFieldComponent,
        RangeEntryFieldComponent,
        TextEntryFieldComponent,
        OkDialogComponent,
        TransformerErrorDialogComponent,
        OrganizationsSessiontimeoutComponent,
        DeactivateOrganizationDialogComponent,
        AutologoutConfirmDialogComponent,
        SelectFilterOptionComponent,
        UserListViewUnassignedComponent,
        FindReplaceChooserMapDialogOptionComponent,
        AutocompleteFieldWithMatformComponent,
        ParquetDataConnectorComponent,
        ProgressBusyIndicatorComponent,
        JsonPasteDialogComponent,
        SmartSampleDialogComponent,
        AuditCompilerDialogComponent,
        AuditRuleDetailDialogComponent,
        FileUploadDialogComponent,
        ImportRefineryDialogComponent,
        DelimiterDialogComponent,
        FtpServiceFormComponent,
        FtpServiceSelectComponent,
        OrganizationSettingsComponent,
        FtpSetupComponent,
        FtpServiceDialogComponent,
        ExpressionEditorComponent,
        MaplibraryComponent,
        AutocompleteFieldMapperComponent,
        ParquetpartitionFolderBrowserDialogComponent
    ],
    bootstrap: [AppComponent],
    exports: [ColumnResizeDirective], imports: [NgxGraphModule,
        DndDirective,
        CopilotModule,
        MatSelectModule,
        ScrollingModule,
        BrowserModule,
        BrowserAnimationsModule,
        ReactiveFormsModule,
        FormsModule,
        NgxMaterialTimepickerModule,
        MatExpansionModule,
        QuillModule.forRoot(),
        RouterModule.forRoot([
            // { path: '', redirectTo: 'sign-in', pathMatch: 'full' }, // Default route to login
            { path: '', component: LoginComponent, canActivate: [LoginGuard] },
            //{ path: 'sign-in', component: LoginComponent},
            { path: 'node', component: NodedefinitionComponent },
            { path: 'forgot-password', component: ForgotPasswordComponent },
            { path: 'reset-password/:id', component: ResetPasswordComponent }, // With parameter
            { path: 'reset-password', component: ResetPasswordComponent }, // Without parameter
            { path: 'emailtest', component: EmailSenderComponent },
            //  { path: '', component: NodedefinitionComponent},
            { path: 'refresh', component: RefreshComponent },
            { path: 'devsettings', component: DevsettingsComponent }, //for devloper
            { path: 'importData', component: ImportDataComponent },
            { path: 'modules', component: ModulesComponent },
            { path: 'devData', component: DevelopmentDataComponent },
            { path: 'clients', component: ClientsComponent },
            { path: 'errorLog', component: ErrorLogComponent },
            { path: 'settings', component: SettingsComponent,
                children: [
                    { path: 'environment', component: EnvironmentSettingsComponent },
                    { path: 'developer', component: DeveloperSettingsComponent },
                    { path: 'copilot', component: CopilotSettingsComponent },
                    { path: 'users', component: UserListViewComponent },
                    { path: 'usersunassigned', component: UserListViewUnassignedComponent, canActivate: [AuthGuard] },
                    { path: 'organization', component: OrganizationListViewComponent },
                    { path: 'profile', component: UserProfileComponent },
                    { path: 'change-password', component: ChangePasswordComponent },
                      { path: 'organization-settings', component: OrganizationSettingsComponent }
                ] },
            { path: 'scheduler', component: SchedulerComponent },
            { path: 'configs', component: ConfigListViewComponent },
            { path: 'dataTransformConfig/:configId',
                component: DataTransformConfigLayoutComponent,
                data: { animation: 'DataTransformConfigPage' },
                children: [
                    { path: 'ingest', component: IngestWorkFlowLayoutComponent },
                    { path: 'mapper', component: MapperFlowLayoutComponent },
                    { path: 'transform', component: TransformerWorkFlowLayoutComponent },
                    { path: 'finalize', component: TransformerWorkFlowLayoutComponent },
                    { path: 'extractorTransformDetail/:transfomConfigId', component: ExtractorTransformDetailComponent, canDeactivate: [PendingChangesGuard] },
                    { path: 'activityNamingTransformDetail/:transfomConfigId', component: ActivityNamingTransformDetailComponent, canDeactivate: [PendingChangesGuard] },
                    { path: 'eventGroupNamerTransformDetail/:transfomConfigId', component: EventGroupNamerTransformDetailComponent, canDeactivate: [PendingChangesGuard] },
                    { path: 'eventNamerTransformDetail/:transfomConfigId', component: EventNamerTransformDetailComponent, canDeactivate: [PendingChangesGuard] },
                    { path: 'groupAttributorTransformDetail/:transfomConfigId', component: GroupAttributorTransformDetailComponent, canDeactivate: [PendingChangesGuard] },
                    { path: 'columnCalcTransformDetail/:transfomConfigId', component: ColumnCalcTransformDetailComponent, canDeactivate: [PendingChangesGuard] },
                    { path: 'columnLookupTransformDetail/:transfomConfigId', component: ColumnLookupTransformDetailComponent, canDeactivate: [PendingChangesGuard] },
                    { path: 'farmConverterTransformDetail/:transfomConfigId', component: FarmConverterTransformDetailComponent, canDeactivate: [PendingChangesGuard] },
                    { path: 'joinerTransformDetail/:transfomConfigId', component: JoinerTransformDetailComponent, canDeactivate: [PendingChangesGuard] },
                    { path: 'rowFillerTransformDetail/:transfomConfigId', component: RowFillerTransformDetailComponent, canDeactivate: [PendingChangesGuard] },
                    { path: 'anonymizerTransformDetail/:transfomConfigId', component: AnonymizerTransformDetailComponent, canDeactivate: [PendingChangesGuard] },
                    { path: 'columnCleanerTransformDetail/:transfomConfigId', component: ColumnCleanerTransformDetailComponent, canDeactivate: [PendingChangesGuard] },
                    { path: 'configDetail', component: ConfigDetailComponent }
                ] },
            { path: 'transformDetailJsonViewer', component: TransformDetailJsonViewerComponent, outlet: 'sidebar' },
            { path: 'errorReportDetailView', component: ErrorLogDetailViewComponent, outlet: 'sidebar' },
            // { path: 'extractorTransformDetail', component: ExtractorTransformDetailComponent }
            { path: '**', redirectTo: '' } // Redirect undefined paths to the root
        ]),
        // RouterModule.forChild([
        //   {
        //     path: 'builds/:buildDefinitionId',
        //     component: BuildDefinitionComponent,
        //     children: [
        //       { path: 'transformDataTaskEditor', component: TransformDataTaskEditorComponent }
        //     ]
        //   }
        // ]),
        LayoutModule,
        MaterialModule,
        FlexLayoutModule,
        DragDropModule], providers: [
        DatePipe,
        ModulesService,
        ClientsService,
        ConfigService,
        ErrorReportingService,
        NavigationService,
        EnvironmentService,
        DataDictionaryService,
        TransformPipelineService,
        ConfigViewModelService,
        EventBrokerService,
        [PendingChangesGuard],
        AppEnvService,
        {
            provide: IStorageService,
            useClass: StorageService
        },
        provideAppInitializer(() => {
        const initializerFn = ((env: AppEnvService) => () => env.load())(inject(AppEnvService));
        return initializerFn();
      }),
        provideHttpClient(withInterceptorsFromDi()),
        { provide: HTTP_INTERCEPTORS, useClass: CredentialsInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: ConfigApiInterceptor, multi: true },
        { provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useFactory: matTooltipDefaults },
    ] })
export class AppModule {

}

/*
Copyright Google LLC. All Rights Reserved.
Use of this source code is governed by an MIT-style license that
can be found in the LICENSE file at https://angular.io/license
*/
